import React, { useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, RadialGradient, Stop } from 'react-native-svg';
import { useTheme } from '../../context/ThemeContext';
import { ensureLoaderKeyframes } from './loaderWebKeyframes';

const SHOW_DELAY_MS = 400;
const ORBIT_DURATION_MS = 2800;
/** Тот же ход при «уменьшить движение», только спокойнее. */
const REDUCED_ORBIT_DURATION_MS = 4600;
const GLOW_DURATION_MS = 2400;

/**
 * На вебе знак крутит CSS (см. `loaderWebKeyframes`), а не `Animated`.
 * `Animated` без нативного драйвера считает кадры в JS-потоке — а лоадер
 * показывается ровно тогда, когда этот поток занят чанком маршрута или
 * бутстрапом сессии: знак замирал на всё время загрузки.
 */
const IS_WEB = Platform.OS === 'web';
const WEB_ANIM = (name: 'agpLoaderEnter' | 'agpLoaderPulse' | 'agpLoaderOrbit') =>
  IS_WEB ? ({ dataSet: { [name]: 'true' } } as any) : {};

ensureLoaderKeyframes();

/**
 * Плотность знака. Веб-калибровка CORE-973 утверждена и не меняется: на
 * мониторе тонкие линии читаются. На телефоне тот же знак почти растворялся в
 * светлом фоне — экран меньше, а смотрят на него часто при дневном свете,
 * поэтому нативка получает свои, более плотные значения.
 */
const DENSE = Platform.OS !== 'web';
const RING_STROKE_OPACITY = { light: DENSE ? 0.26 : 0.14, dark: DENSE ? 0.32 : 0.18 };
const RING_STROKE_WIDTH = DENSE ? 1.4 : 1;
/** Неподвижный след орбиты под бегущей дугой. */
const TRACK_OPACITY = DENSE ? 0.2 : 0.12;
const ARC_STROKE_WIDTH = DENSE ? 2.6 : 1.5;
const ARC_STOPS = DENSE
  ? { start: 0.18, mid: 0.88, end: 1 }
  : { start: 0.08, mid: 0.62, end: 0.82 };
const DOT_RADIUS = DENSE ? 3.2 : 2.6;
const DOT_GLOW_RADIUS = DENSE ? 8 : 7;
const DOT_GLOW_OPACITY = DENSE ? 0.75 : 0.58;
const HALO_COLOR = {
  light: DENSE ? 'rgba(233,130,69,0.05)' : 'rgba(233,130,69,0.025)',
  dark: DENSE ? 'rgba(233,130,69,0.06)' : 'rgba(233,130,69,0.035)',
};

type GlobalPageLoaderProps = {
  /** Skip the anti-flash delay in deterministic previews/tests only. */
  immediate?: boolean;
};

/**
 * CORE-973 — the single page-level loading mark for web routes and app boot.
 * Action/button spinners deliberately remain local: this component is only for
 * transitions where an entire page chunk or the stored session is pending.
 */
export function GlobalPageLoader({ immediate = false }: GlobalPageLoaderProps) {
  const { colors, isDark } = useTheme();
  const { width } = useWindowDimensions();
  const markSize = width < 480 ? 64 : 80;
  const markInset = (80 - markSize) / 2;
  const [visible, setVisible] = useState(immediate);
  const [reduceMotion, setReduceMotion] = useState(false);
  const orbit = useRef(new Animated.Value(0)).current;
  const glow = useRef(new Animated.Value(0)).current;
  const entrance = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (immediate) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [immediate]);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then((enabled) => {
      if (mounted) setReduceMotion(enabled);
    });
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (IS_WEB || !visible) return;
    entrance.setValue(0);
    Animated.timing(entrance, {
      toValue: 1,
      duration: 210,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [entrance, visible]);

  useEffect(() => {
    if (IS_WEB) return;
    if (!visible) {
      orbit.stopAnimation();
      glow.stopAnimation();
      orbit.setValue(0);
      glow.setValue(0.35);
      return;
    }

    // «Уменьшить движение» не значит «замереть»: вращение — единственный признак,
    // что страница жива. При включённой настройке остаётся ровный медленный ход,
    // уходит пульсация. Веб-половина этого правила — в loaderWebKeyframes.
    const orbitLoop = Animated.loop(
      Animated.timing(orbit, {
        toValue: 1,
        duration: reduceMotion ? REDUCED_ORBIT_DURATION_MS : ORBIT_DURATION_MS,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    if (reduceMotion) {
      glow.stopAnimation();
      glow.setValue(0.35);
      orbitLoop.start();
      return () => orbitLoop.stop();
    }

    const glowLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(glow, {
          toValue: 1,
          duration: GLOW_DURATION_MS / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(glow, {
          toValue: 0,
          duration: GLOW_DURATION_MS / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    orbitLoop.start();
    glowLoop.start();
    return () => {
      orbitLoop.stop();
      glowLoop.stop();
    };
  }, [glow, orbit, reduceMotion, visible]);

  return (
    <View
      style={[styles.stage, { backgroundColor: colors.pageBg }]}
      accessibilityRole="progressbar"
      accessibilityLabel="Loading"
      // `aria-busy`, а не `accessibilityState={{ busy: true }}`: react-native-web
      // 0.21 больше не разбирает `accessibilityState` — проп молча пропадал, и в
      // DOM во время загрузки не было ни одного aria-busy. Нативка получает то же
      // состояние: RN сам сливает `aria-*` обратно в `accessibilityState`.
      aria-busy
      testID="global-page-loader"
    >
      {visible ? (
        <Animated.View
          style={[
            styles.glow,
            {
              opacity: IS_WEB ? 1 : entrance,
              backgroundColor: isDark ? HALO_COLOR.dark : HALO_COLOR.light,
            },
          ]}
          {...WEB_ANIM('agpLoaderEnter')}
        >
          <Animated.View
            style={
              IS_WEB
                ? undefined
                : {
                    opacity: glow.interpolate({ inputRange: [0, 1], outputRange: [0.72, 1] }),
                    transform: [
                      { scale: glow.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1.025] }) },
                    ],
                  }
            }
            {...WEB_ANIM('agpLoaderPulse')}
          >
            <Svg width={markSize} height={markSize} viewBox="0 0 80 80" accessibilityElementsHidden>
              <Circle
                cx="40"
                cy="40"
                r="28"
                fill="none"
                stroke={isDark ? '#F7C96B' : colors.solar}
                strokeOpacity={isDark ? RING_STROKE_OPACITY.dark : RING_STROKE_OPACITY.light}
                strokeWidth={RING_STROKE_WIDTH}
              />
            </Svg>
          </Animated.View>
          <Animated.View
            style={[
              styles.orbit,
              {
                width: markSize,
                height: markSize,
                top: 12 + markInset,
                left: 12 + markInset,
                ...(IS_WEB
                  ? null
                  : {
                      transform: [{
                        rotate: orbit.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }),
                      }],
                    }),
              },
            ]}
            {...WEB_ANIM('agpLoaderOrbit')}
          >
            <Svg width={markSize} height={markSize} viewBox="0 0 80 80" accessibilityElementsHidden>
              <Defs>
                <LinearGradient id="solarArc" x1="18" y1="20" x2="66" y2="28" gradientUnits="userSpaceOnUse">
                  <Stop offset="0%" stopColor={colors.solar} stopOpacity={ARC_STOPS.start} />
                  <Stop offset="58%" stopColor={colors.solar} stopOpacity={ARC_STOPS.mid} />
                  <Stop offset="100%" stopColor="#F7C96B" stopOpacity={ARC_STOPS.end} />
                </LinearGradient>
                <RadialGradient id="movingOrbitGlow" cx="50%" cy="50%" rx="50%" ry="50%">
                  <Stop offset="0%" stopColor="#F7C96B" stopOpacity="1" />
                  <Stop offset="42%" stopColor={colors.solar} stopOpacity="0.95" />
                  <Stop offset="100%" stopColor={colors.solar} stopOpacity="0" />
                </RadialGradient>
              </Defs>
              <Path
                d="M20 20 A28 28 0 0 1 66 28"
                fill="none"
                stroke={colors.solar}
                strokeOpacity={TRACK_OPACITY}
                strokeWidth="5"
                strokeLinecap="round"
              />
              <Path
                d="M20 20 A28 28 0 0 1 66 28"
                fill="none"
                stroke="url(#solarArc)"
                strokeWidth={ARC_STROKE_WIDTH}
                strokeLinecap="round"
              />
              <Circle cx="66" cy="28" r={DOT_GLOW_RADIUS} fill="url(#movingOrbitGlow)" opacity={DOT_GLOW_OPACITY} />
              <Circle cx="66" cy="28" r={DOT_RADIUS} fill="#F7C96B" />
            </Svg>
          </Animated.View>
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  stage: {
    flex: 1,
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
  },
  glow: {
    width: 104,
    height: 104,
    borderRadius: 52,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#E98245',
    shadowOpacity: 0.12,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 0 },
  },
  orbit: {
    position: 'absolute',
    width: 80,
    height: 80,
  },
});
